/**
 * Eight sample plumbing enquiries — the mix a Madison plumber actually sees in
 * a fortnight: two emergencies, a water heater, a remodel quote, a couple of
 * drains, and one that went nowhere.
 */

import type { Lead } from '../../types';
import { DEMO_WEBSITE_ID } from './site';
import { daysAgo, hoursAgo } from './time';

export const demoLeads: Lead[] = [
  {
    id: 'demo-lead-1',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Marcus Webb',
    email: 'marcus.webb@example.com',
    phone: '(608) 555-0143',
    source: 'Contact Form',
    status: 'new',
    service: 'Emergency repair',
    message:
      'Water is coming up around the base of the toilet in the downstairs bathroom and the floor is wet. Shut the valve off for now. Can someone come out today?',
    urgency: 'emergency',
    notes: null,
    createdAt: hoursAgo(3),
    updatedAt: hoursAgo(3),
    contactedAt: null,
    convertedAt: null,
    viewedAt: null,
  },
  {
    id: 'demo-lead-2',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Priya Raman',
    email: 'praman@example.com',
    phone: '(608) 555-0117',
    source: 'Quick Contact',
    status: 'new',
    service: 'Water heater replacement',
    message:
      'Our water heater is 14 years old and only gives about ten minutes of hot water. Looking for a quote on a replacement — gas, 50 gallon.',
    urgency: 'this week',
    notes: null,
    createdAt: hoursAgo(20),
    updatedAt: hoursAgo(20),
    contactedAt: null,
    convertedAt: null,
    viewedAt: null,
  },
  {
    id: 'demo-lead-3',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Dana Kowalczyk',
    email: 'dana.k@example.com',
    phone: '(608) 555-0176',
    source: 'Booking Request',
    status: 'contacted',
    service: 'Kitchen drain cleaning',
    message: 'Kitchen sink drains very slowly, both basins. Tried a plunger and store-bought cleaner.',
    urgency: 'this week',
    notes: 'Called back — booked for Thursday morning.',
    createdAt: daysAgo(2),
    updatedAt: daysAgo(2),
    contactedAt: daysAgo(2),
    convertedAt: null,
    viewedAt: daysAgo(2),
  },
  {
    id: 'demo-lead-4',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Tom Ridgway',
    email: null,
    phone: '(608) 555-0192',
    source: 'Zip Funnel',
    status: 'qualified',
    service: 'Bathroom remodel rough-in',
    message:
      'Gutting the hall bathroom in a 1948 house. Need someone to move the supply lines and set a new tub drain. Contractor starts demo the week after next.',
    urgency: 'this month',
    notes: 'Walkthrough done. Sending a written quote — likely two days of work.',
    createdAt: daysAgo(4),
    updatedAt: daysAgo(3),
    contactedAt: daysAgo(4),
    convertedAt: null,
    viewedAt: daysAgo(4),
  },
  {
    id: 'demo-lead-5',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Elaine Brooks',
    email: 'ebrooks@example.com',
    phone: '(608) 555-0155',
    source: 'Contact Form',
    status: 'converted',
    service: 'Sump pump replacement',
    message: 'Sump pump ran constantly through the last storm and now hums without pumping.',
    urgency: 'emergency',
    notes: 'Replaced pump and check valve. Paid on site.',
    createdAt: daysAgo(8),
    updatedAt: daysAgo(7),
    contactedAt: daysAgo(8),
    convertedAt: daysAgo(7),
    viewedAt: daysAgo(8),
  },
  {
    id: 'demo-lead-6',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Hector Alvarez',
    email: 'halvarez@example.com',
    phone: '(608) 555-0138',
    source: 'referral',
    status: 'converted',
    service: 'Outdoor spigot repair',
    message: 'Frost-proof spigot on the north side split over the winter. Neighbour recommended you.',
    urgency: 'this month',
    notes: 'Cut in a new sillcock. Invoiced $265.',
    createdAt: daysAgo(11),
    updatedAt: daysAgo(10),
    contactedAt: daysAgo(11),
    convertedAt: daysAgo(10),
    viewedAt: daysAgo(11),
  },
  {
    id: 'demo-lead-7',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Grace Lindqvist',
    email: 'g.lindqvist@example.com',
    phone: '(608) 555-0129',
    source: 'Contact Form',
    status: 'contacted',
    service: 'Garbage disposal install',
    message: 'Disposal died. Happy to buy the unit myself if you can fit it.',
    urgency: 'this week',
    notes: 'Left a voicemail Tuesday, texted Wednesday.',
    createdAt: daysAgo(5),
    updatedAt: daysAgo(4),
    contactedAt: daysAgo(4),
    convertedAt: null,
    viewedAt: daysAgo(5),
  },
  {
    id: 'demo-lead-8',
    websiteId: DEMO_WEBSITE_ID,
    name: 'Owen Bracket',
    email: 'owen.bracket@example.com',
    phone: null,
    source: 'Quick Contact',
    status: 'lost',
    service: 'Whole-house repipe',
    message: 'Getting prices for repiping a duplex in galvanised. What is your ballpark per unit?',
    urgency: 'no rush',
    notes: 'Went with a lower bid. Worth a follow-up in the spring.',
    createdAt: daysAgo(16),
    updatedAt: daysAgo(13),
    contactedAt: daysAgo(15),
    convertedAt: null,
    viewedAt: daysAgo(15),
  },
];

export const demoUnreadLeadCount = demoLeads.filter(lead => !lead.viewedAt).length;
